import { CharStreams, CommonTokenStream } from 'antlr4';
import QueryLexer from '../ql/QueryLexer';
import QueryParser from '../ql/QueryParser';
import QueryParserVisitor from '../ql/QueryParserVisitor';
function single(a) {
    return Array.isArray(a) ? a[0] : a;
}
class NaryTransformVisitor extends QueryParserVisitor {
    constructor() {
        super(...arguments);
        this.visitQuery = (ctx) => {
            return single(this.visit(ctx.expression()));
        };
        this.visitBracketExpression = (ctx) => {
            return single(this.visit(ctx.expression()));
        };
        this.visitAndExpression = (ctx) => {
            const left = single(this.visit(ctx.expression(0)));
            const right = single(this.visit(ctx.expression(1)));
            let operands = [];
            if (left.operator && left.operator === 'AND') {
                operands.push(...left.operands);
            }
            else {
                operands.push(left);
            }
            if (right.operator && right.operator === 'AND') {
                operands.push(...right.operands);
            }
            else {
                operands.push(right);
            }
            return { operator: 'AND', operands: operands };
        };
        this.visitNotExpression = (ctx) => {
            return { operator: 'NOT', operands: [single(this.visit(ctx.expression()))] };
        };
        this.visitOrExpression = (ctx) => {
            const left = single(this.visit(ctx.expression(0)));
            const right = single(this.visit(ctx.expression(1)));
            let operands = [];
            if (left.operator && left.operator === 'OR') {
                operands.push(...left.operands);
            }
            else {
                operands.push(left);
            }
            if (right.operator && right.operator === 'OR') {
                operands.push(...right.operands);
            }
            else {
                operands.push(right);
            }
            return { operator: 'OR', operands: operands };
        };
        this.visitTerm = (ctx) => {
            return single(this.visitChildren(ctx));
        };
        this.visitTagIdTerm = (ctx) => {
            return { tagId: parseInt(ctx.tagIdValue().getText()) };
        };
        this.visitTagNameTerm = (ctx) => {
            return { tagName: ctx.tagNameValue().getText() };
        };
        this.visitNoTagTerm = (ctx) => {
            return { noTag: {} };
        };
        this.visitWordPatternTerm = (ctx) => {
            return { wordPattern: ctx.wordPatternValue().getText() };
        };
        this.visitWordLengthTerm = (ctx) => {
            function num(s) {
                return s === undefined || s === null || s === '' ? undefined : parseInt(s);
            }
            const wordLength = ctx.wordLengthRange().getText();
            if (wordLength.includes('..')) {
                const [min, max] = wordLength.split('..');
                return { wordLength: { min: num(min), max: num(max) } };
            }
            else {
                return { wordLength: parseInt(wordLength) };
            }
        };
    }
}
function formatResult(data) {
    if (data === null || data === undefined) {
        return 'null';
    }
    if (data.operator) {
        const op = data.operator;
        const args = data.operands;
        const formattedArgs = args.map(formatResult);
        return `${op}(${formattedArgs.join(', ')})`;
    }
    if (data.tagId) {
        return `ti:${data.tagId}`;
    }
    if (data.tagName) {
        return `tn:${data.tagName}`;
    }
    if (data.noTag) {
        return 'nt';
    }
    if (data.wordPattern) {
        return `wp:${data.wordPattern}`;
    }
    if (data.wordLength) {
        const wordLength = data.wordLength;
        if (typeof wordLength === 'number') {
            return `wl:${wordLength}`;
        }
        else {
            const min = wordLength.min !== undefined ? wordLength.min : '';
            const max = wordLength.max !== undefined ? wordLength.max : '';
            return `wl:${min}..${max}`;
        }
    }
    throw new Error(`Unknown data type: ${data}`);
}
function buildDetails(data, tags) {
    if (data === null || data === undefined) {
        return [];
    }
    if (data.operator) {
        const op = data.operator;
        const args = data.operands;
        const details = args.flatMap(arg => buildDetails(arg, tags));
        return [{
                criteria: details,
                logic: op
            }];
    }
    if (data.tagId) {
        return [{
                condition: '=',
                type: 'tag',
                value: [data.tagId.toString()]
            }];
    }
    if (data.tagName) {
        const tag = tags.find(t => t.displayName === data.tagName);
        if (tag === undefined || tag === null) {
            return [];
        }
        return [{
                condition: '=',
                type: 'tag',
                value: [tag.id.toString()]
            }];
    }
    if (data.noTag) {
        return [{
                condition: 'null',
                type: 'tag'
            }];
    }
    if (data.wordPattern) {
        return [{
                condition: 'is',
                type: 'word-pattern',
                value: [data.wordPattern]
            }];
    }
    if (data.wordLength) {
        const wordLength = data.wordLength;
        if (typeof wordLength === 'number') {
            return [{
                    condition: '=',
                    type: 'word-length',
                    value: [wordLength.toString()]
                }];
        }
        else {
            if (wordLength.min === undefined && wordLength.max === undefined) {
                return [];
            }
            if (wordLength.max === undefined) {
                return [{
                        condition: '>=',
                        type: 'word-length',
                        value: [wordLength.min.toString()]
                    }];
            }
            if (wordLength.min === undefined) {
                return [{
                        condition: '<=',
                        type: 'word-length',
                        value: [wordLength.max.toString()]
                    }];
            }
            return [{
                    condition: 'between',
                    type: 'word-length',
                    value: [wordLength.min.toString(), wordLength.max.toString()]
                }];
        }
    }
    throw new Error(`Unknown data type: ${data}`);
}
export function transformQuery(query, tags) {
    if (!query) {
        return null;
    }
    const chars = CharStreams.fromString(query);
    const lexer = new QueryLexer(chars);
    const tokens = new CommonTokenStream(lexer);
    const parser = new QueryParser(tokens);
    parser.buildParseTrees = true;
    const tree = parser.query();
    const visitor = new NaryTransformVisitor();
    const result = visitor.visit(tree);
    const details = buildDetails(result, tags);
    if (details === null || details === undefined || details.length === 0) {
        return null;
    }
    if (details[0].type) {
        return {
            criteria: details,
            logic: 'AND'
        };
    }
    return details[0];
}
